import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import gsap from "gsap";
import { apiurl } from "../config/config";
import confetti from "canvas-confetti";
import { MANAGER_META, FLOOR_HEADS, FLOOR_MANAGERS } from "../json/salesTarget";

const CAMPAIGN_CONFIG = {
    startDate: "2026-03-01",
    targets: {
        GMIT: 28500, // Rahul
        ALTF: 46000, // Anand
    },
};

const MANAGER_VIDEO_BATTLES = [
    {
        A: "JayVijay5099",
        B: "SanjayRewani1619",
        videos: {
            A: "/videos/JayWon.gif",
            B: "/videos/SanjayWon.gif",
            DRAW: "/videos/draw.gif",
        },
        names: {
            A: "Jay",
            B: "Sanjay",
        },
    },
];

const getBadge = (pos) => {
    if (pos === 0)
        return {
            text: "🥇 GOLD LEADER",
            color: "bg-yellow-400 text-black",
        };

    if (pos === 1)
        return {
            text: "🥈 SILVER",
            color: "bg-slate-300 text-black",
        };

    if (pos === 2)
        return {
            text: "🥉 BRONZE",
            color: "bg-orange-400 text-black",
        };

    return {
        text: "⭐ TOP PERFORMER",
        color: "bg-indigo-600 text-white",
    };
};

const MANAGER_BATTLES = [
    { A: "Abhishek4629", B: "SanjayRewani1619" },
    { A: "Himanshu1203", B: "ASHWANI3434" },
];

const floorWinnerByPercentage = (aValue, aTarget, bValue, bTarget) => {
    const aPct = (aValue / aTarget) * 100;
    const bPct = (bValue / bTarget) * 100;

    const diff = Math.abs(aPct - bPct);

    // treat as draw if difference < 0.5%
    if (diff < 0.5) return "DRAW";

    return aPct > bPct ? "A" : "B";
};
/* ================= UTILS ================= */

const winnerOf = (a, b) => (a === b ? "DRAW" : a > b ? "A" : "B");

const getProgressMeta = (p) => {
    if (p >= 100) return { color: "#16a34a", text: "TARGET ACHIEVED" };
    if (p >= 90) return { color: "#22c55e", text: "EXCELLENT" };
    if (p >= 80) return { color: "#4ade80", text: "VERY GOOD" };
    if (p >= 70) return { color: "#a3e635", text: "ON TRACK" };
    if (p >= 60) return { color: "#facc15", text: "NEEDS PUSH" };
    if (p >= 50) return { color: "#fbbf24", text: "NEEDS PUSH" };
    if (p >= 40) return { color: "#fb923c", text: "CRITICAL PUSH" };
    if (p >= 30) return { color: "#f97316", text: "CRITICAL PUSH" };
    if (p >= 20) return { color: "#ef4444", text: "ALERT" };
    return { color: "#dc2626", text: "HIGH ALERT" };
};

const getBattleCommentary = (A, B, aVal, bVal) => {
    if (aVal === bVal)
        return {
            headline: "It's a Perfect Draw 🤝",
            sub: "Anyone can take the lead with 1 order!",
        };
    const leaderId = aVal > bVal ? A : B;
    const trailerId = aVal > bVal ? B : A;
    const diff = Math.abs(aVal - bVal);
    return {
        headline: `${MANAGER_META[leaderId].name} is leading by +${diff} orders`,
        sub: `${MANAGER_META[trailerId].name} needs ${diff + 1} more orders to WIN 🏆`,
    };
};

/* ================= SCALE HOOK ================= */

function useTVScale() {
    const [scale, setScale] = useState(1);
    useEffect(() => {
        const updateScale = () => setScale(window.innerWidth / 1920);
        updateScale();
        window.addEventListener("resize", updateScale);
        return () => window.removeEventListener("resize", updateScale);
    }, []);
    return scale;
}

const managerWinnerByPercentage = (managers) => {
    const enriched = managers.map((m) => {
        const target = MANAGER_META[m.id].target;
        const pct = target > 0 ? (m.value / target) * 100 : 0;
        return { ...m, pct };
    });

    const sorted = [...enriched].sort((a, b) => b.pct - a.pct);

    const leader = sorted[0];
    const runnerUp = sorted[1];

    // tolerance for real draw (0.5%)
    const isDraw = runnerUp && Math.abs(leader.pct - runnerUp.pct) < 0.5;

    if (isDraw) {
        return enriched.map((m) => ({ ...m, status: "DRAW" }));
    }

    return enriched.map((m) => ({
        ...m,
        status: m.id === leader.id ? "WIN" : "LOSE",
    }));
};

export default function WideScreenAnimated() {
    const [showWinnerVideo, setShowWinnerVideo] = useState(false);
    const [winnerFloor, setWinnerFloor] = useState(null);

    const [showManagerVideo, setShowManagerVideo] = useState(false);
    const [managerWinner, setManagerWinner] = useState(null);

    const [campaignData, setCampaignData] = useState({});
    const [managerLoginData, setManagerLoginData] = useState([]);

    const scale = useTVScale();
    const [managerOrders, setManagerOrders] = useState({});
    const [managersDetails, setManagersDetails] = useState([]);
    const [slideType, setSlideType] = useState("INTRO");
    //   const [battleIndex, setBattleIndex] = useState(0);
    //   const [floorIndex, setFloorIndex] = useState(0);
    const [groupIndex, setGroupIndex] = useState(0);

    const fetchManagerLoginSummary = async () => {
        try {
            const res = await axios.get(
                `${apiurl}/utils/crm/getManagerAgentSummary`, // your backend route
            );

            if (res.data?.success) {
                setManagerLoginData(res.data.data.data || []);
                console.log("Manager Login Summary:", res.data.data);
            }
        } catch (err) {
            console.error("Login Summary Error:", err);
        }
    };

    useEffect(() => {
        fetchManagerLoginSummary(); // initial call

        const interval = setInterval(
            () => {
                fetchManagerLoginSummary();
            },
            60 * 10 * 1000,
        ); // every 10 minutes

        return () => clearInterval(interval);
    }, []);

    const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD

    const [teamData, setTeamData] = useState([]);

    const fetchPerformance = async () => {
        try {
            const baseParams = {
                from_date: today,
                to_date: today,
                agent_ids: "",
                manager_ids: "",
            };

            const [dcRes, ppRes] = await Promise.all([
                axios.get(`${apiurl}/utils/crm/realTimeOrderData`, {
                    params: { ...baseParams, tl_ids: "Superdc" },
                }),
                axios.get(`${apiurl}/utils/crm/realTimeOrderData`, {
                    params: { ...baseParams, tl_ids: "Super_Paachan_Pro" },
                }),
            ]);

            const dcOrders = dcRes?.data?.data?.result?.data?.total?.totalOrders || 0;

            const ppOrders = ppRes?.data?.data?.result?.data?.total?.totalOrders || 0;

            const teams = [
                {
                    team_name: "SUPER_DC_TEAM",
                    orders: dcOrders,
                    target: 240,
                    image: "/images/superDCTeam.png",
                },
                {
                    team_name: "SUPER_PP_TEAM",
                    orders: ppOrders,
                    target: 240,
                    image: "/images/superPPTeam.png",
                },
            ];

            setTeamData(teams);
        } catch (error) {
            console.error("Team API Error:", error);
        }
    };
    useEffect(() => {
        fetchPerformance();

        const interval = setInterval(fetchPerformance, 50000);

        return () => clearInterval(interval);
    }, []);

    const managerLoginMap = useMemo(() => {
        const map = {};
        managerLoginData.forEach((m) => {
            map[m.user_name] = m; // ✅ MUST MATCH m.id
        });
        return map;
    }, [managerLoginData]);

    const managerDetailsMap = useMemo(() => {
        const map = {};
        managersDetails.forEach((m) => {
            map[m.o_manager_name] = m; // ✅ IMPORTANT
        });
        return map;
    }, [managersDetails]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // ===== DATES =====
                const today = new Date().toISOString().split("T")[0];
                const campaignStart = CAMPAIGN_CONFIG.startDate;

                // ===== PARALLEL API CALLS (FAST) =====
                const [dailyRes, campaignRes] = await Promise.all([
                    axios.get(`${apiurl}/utils/crm/realTimeOrderData2`, {
                        headers: { token: "ad2355a9d54335e27e2c3a3ed5b6e381" },
                        params: {
                            from_date: today,
                            to_date: today,
                        },
                    }),

                    axios.get(`${apiurl}/utils/crm/realTimeOrderData2`, {
                        headers: { token: "ad2355a9d54335e27e2c3a3ed5b6e381" },
                        params: {
                            from_date: campaignStart,
                            to_date: today,
                        },
                    }),
                ]);

                /* ===== DAILY DATA ===== */
                const dailyList = dailyRes?.data?.data?.result?.data?.managers || [];

                setManagersDetails(dailyList);

                const dailyMap = {};
                dailyList.forEach((m) => {
                    dailyMap[m.o_manager_name] = Number(m.totalOrders || 0);
                });

                setManagerOrders(dailyMap);

                /* ===== CAMPAIGN DATA ===== */
                const campaignList =
                    campaignRes?.data?.data?.result?.data?.managers || [];

                const campaignMap = {};

                campaignList.forEach((m) => {
                    campaignMap[m.o_manager_name] = Number(m.totalOrders || 0);
                });

                setCampaignData(campaignMap);

                console.log("Campaign params:", {
                    from_date: campaignStart,
                    to_date: today,
                });
            } catch (err) {
                console.error("Fetch error:", err);
            }
        };

        // first load
        fetchData();

        // auto refresh
        const api = setInterval(fetchData, 30000);

        return () => clearInterval(api);
    }, []);

    /* ---------- FLOOR DATA ---------- */
    const floorData = useMemo(() => {
        const sum = (arr) => arr.reduce((a, b) => a + (managerOrders[b] || 0), 0);
        return { GMIT: sum(FLOOR_MANAGERS.GMIT), ALTF: sum(FLOOR_MANAGERS.ALTF) };
    }, [managerOrders]);

    const campaignFloorData = useMemo(() => {
        const sum = (arr) => arr.reduce((a, b) => a + (campaignData[b] || 0), 0);

        return {
            GMIT: sum(FLOOR_MANAGERS.GMIT),
            ALTF: sum(FLOOR_MANAGERS.ALTF),
        };
    }, [campaignData]);

    const MANAGER_GROUPS = [
        ["SanjayRewani1619", "JayVijay5099"],
        ["Himanshu1203", "ASHWANI3434"],
        ["Abhishek4629", "LavitBhati714"],
    ];

    const duration = slideType === "CAMPAIGN" ? 15000 : 10000;

    //   useEffect(() => {

    //   if (showWinnerVideo) return;

    //     const timer = setTimeout(() => {
    //       // INTRO runs only once
    //       if (slideType === "INTRO") {
    //         setSlideType("FLOOR");
    //       }

    //       // FLOOR → MANAGER
    //       else if (slideType === "FLOOR") {
    //         setSlideType("MANAGER");
    //         setGroupIndex(0);
    //       }

    //       // MANAGER groups rotation
    //       else if (slideType === "MANAGER") {
    //         if (groupIndex + 1 < MANAGER_GROUPS.length) {
    //           setGroupIndex((g) => g + 1);
    //         } else {
    //           setGroupIndex(0);
    //           setSlideType("CAMPAIGN");
    //         }
    //       } else if (slideType === "CAMPAIGN") {
    //         setSlideType("TEAMWISE");
    //       }

    //       // CAMPAIGN → FLOOR (loop)
    //       else if (slideType === "TEAMWISE") {
    //         setSlideType("FLOOR");
    //       }
    //     // }, duration);
    //     }, 10000);

    //     return () => clearTimeout(timer);
    //   }, [slideType, groupIndex]);

    useEffect(() => {
        if (showWinnerVideo) return;

        const timer = setTimeout(() => {
            if (slideType === "INTRO") {
                setSlideType("FLOOR");
            } else if (slideType === "FLOOR") {
                setSlideType("MANAGER");
                setGroupIndex(0);
            } else if (slideType === "MANAGER") {
                if (groupIndex + 1 < MANAGER_GROUPS.length) {
                    setGroupIndex((g) => g + 1);
                } else {
                    setGroupIndex(0);
                    setSlideType("CAMPAIGN");
                }
            } else if (slideType === "CAMPAIGN") {
                setSlideType("TEAMWISE");
            } else if (slideType === "TEAMWISE") {
                setSlideType("FLOOR");
            }
        }, duration);

        return () => clearTimeout(timer);
    }, [slideType, groupIndex, showWinnerVideo]);

    const currentGroup =
        slideType === "MANAGER" ? MANAGER_GROUPS[groupIndex] : null;

    const managerGroupData = useMemo(() => {
        if (!currentGroup) return [];

        const baseData = currentGroup.map((id) => ({
            id,
            value: managerOrders[id] || 0,
        }));

        return managerWinnerByPercentage(baseData);
    }, [currentGroup, managerOrders]);

    const floorAgentStats = useMemo(() => {
        const calculateFloorStats = (managerIds) => {
            let totalAgents = 0;
            let loginAgents = 0;
            let below3Orders = 0;

            managerIds.forEach((id) => {
                const login = managerLoginMap[id];
                const details = managerDetailsMap[id];

                if (login) {
                    totalAgents += Number(login.total_agents || 0);
                    loginAgents += Number(login.today_login_count || 0);
                }

                if (details) {
                    below3Orders += Number(details.agent_count_below_3 || 0);
                }
            });

            const shrinkage =
                totalAgents > 0 ? ((totalAgents - loginAgents) / totalAgents) * 100 : 0;

            return {
                totalAgents,
                loginAgents,
                shrinkage,
                below3Orders,
            };
        };

        return {
            GMIT: calculateFloorStats(FLOOR_MANAGERS.GMIT),
            ALTF: calculateFloorStats(FLOOR_MANAGERS.ALTF),
        };
    }, [managerLoginMap, managerDetailsMap]);

    const managerOrdersRef = useRef({});

    useEffect(() => {
        managerOrdersRef.current = managerOrders;
    }, [managerOrders]);

    const detectFloorWinner = (data) => {
        const win = floorWinnerByPercentage(
            data.GMIT,
            FLOOR_HEADS[0].target,
            data.ALTF,
            FLOOR_HEADS[1].target,
        );

        if (win === "A") setWinnerFloor("GMIT");
        else if (win === "B") setWinnerFloor("ALTF");
        else setWinnerFloor("DRAW");
    };

    const detectManagerWinner = (orders) => {
        const battle = MANAGER_VIDEO_BATTLES[0];

        const aValue = orders[battle.A] || 0;
        const bValue = orders[battle.B] || 0;

        const win = winnerOf(aValue, bValue);

        if (win === "A") setManagerWinner({ id: "A", battle });
        else if (win === "B") setManagerWinner({ id: "B", battle });
        else setManagerWinner({ id: "DRAW", battle });
    };

    useEffect(() => {
        const playManagerVideo = () => {
            const latestOrders = managerOrdersRef.current;

            if (!latestOrders) return;

            detectManagerWinner(latestOrders);

            setShowManagerVideo(true);

            setTimeout(() => {
                setShowManagerVideo(false);
            }, 10000);
        };

        // const interval = setInterval(playManagerVideo, 2700000);
        const interval = setInterval(playManagerVideo, 90000);

        return () => clearInterval(interval);
    }, []);

    // useEffect(() => {

    //   const runWinnerCheck = () => {

    //     detectFloorWinner();
    //     setShowWinnerVideo(true);

    //     setTimeout(() => {
    //       setShowWinnerVideo(false);
    //     }, 10000);

    //   };

    //   runWinnerCheck(); // run once immediately

    //   const interval = setInterval(runWinnerCheck, 60000);

    //   return () => clearInterval(interval);

    // }, [floorData]);

    const floorDataRef = useRef([]);

    useEffect(() => {
        floorDataRef.current = floorData;
    }, [floorData]);

    // useEffect(() => {

    //   const playWinnerVideo = () => {

    //     detectFloorWinner();
    //     setShowWinnerVideo(true);

    //     setTimeout(() => {
    //       setShowWinnerVideo(false);
    //     }, 10000); // video length

    //   };

    //   const interval = setInterval(playWinnerVideo, 60000); // every 1 minute

    //   return () => clearInterval(interval);

    // }, [floorData]);

    useEffect(() => {
        const playWinnerVideo = () => {
            const latestData = floorDataRef.current;

            // if (!latestData || latestData.length === 0) return;
            if (!latestData.GMIT && !latestData.ALTF) return;

            detectFloorWinner(latestData);

            setShowWinnerVideo(true);

            setTimeout(() => {
                setShowWinnerVideo(false);
            }, 10000);
        };

        // const interval = setInterval(playWinnerVideo, 1800000);
        const interval = setInterval(playWinnerVideo, 60000);

        return () => clearInterval(interval);
    }, []);

    return (
        <div
            style={{
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                width: 1920,
                height: 1080,
            }}
            className="bg-gradient-to-br from-slate-50 via-white to-slate-100 overflow-hidden"
        >
            {/* {showWinnerVideo && <WinnerVideo winner={winnerFloor} />} */}
            {showWinnerVideo && winnerFloor && <WinnerVideo winner={winnerFloor} />}

            {showManagerVideo && managerWinner && (
                <ManagerWinnerVideo data={managerWinner} />
            )}

            {slideType === "INTRO" && <IntroSlide />}
            {slideType === "CAMPAIGN" && (
                <CampaignSlide floorData={campaignFloorData} />
            )}
            {slideType === "TEAMWISE" && <TeamWiseCard data={teamData} />}

            {slideType === "FLOOR" && (
                <FloorSlide floorData={floorData} floorAgentStats={floorAgentStats} />
            )}

            {slideType === "MANAGER" && (
                <ManagerGroupSlide
                    managers={managerGroupData}
                    managerDetailsMap={managerDetailsMap}
                    managerLoginMap={managerLoginMap}
                />
            )}
        </div>
    );
}

/* ================= INTRO SLIDE ================= */

function IntroSlide() {
    return (
        <SlideWrapper>
            <motion.div
                className="text-center mb-16"
                initial={{ opacity: 0, y: -40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
            >
                <motion.h1
                    animate={{ scale: [1, 1.04, 1] }}
                    transition={{ repeat: Infinity, duration: 1.6 }}
                    className="text-[96px] font-black tracking-tight bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent"
                >
                    🚀 Today’s Sales Battle
                </motion.h1>
                <p className="mt-6 text-[40px] font-semibold text-gray-600">
                    Live • Competitive • High Energy
                </p>
            </motion.div>

            <div className="grid grid-cols-2 gap-24 mt-10">
                {FLOOR_HEADS.map((f) => (
                    <motion.div
                        key={f.key}
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="bg-white rounded-[36px] p-12 text-center shadow-xl"
                    >
                        <img
                            src={f.photo}
                            className="w-64 h-64 mx-auto rounded-full border-4 border-indigo-500"
                        />
                        <h2 className="text-[52px] font-black mt-6">{f.name}</h2>
                        <p className="text-[32px] text-gray-600">
                            Target: <b>{f.target}</b>
                        </p>
                    </motion.div>
                ))}
            </div>
        </SlideWrapper>
    );
}

/* ================= FLOOR SLIDE ================= */

function FloorSlide({ floorData, floorAgentStats }) {
    //   const win = winnerOf(floorData.GMIT, floorData.ALTF);
    const win = floorWinnerByPercentage(
        floorData.GMIT,
        FLOOR_HEADS[0].target,
        floorData.ALTF,
        FLOOR_HEADS[1].target,
    );

    return (
        <SlideWrapper>
            <h1 className="text-6xl font-black text-center mb-10">
                Live Floor Performance
            </h1>
            <div className="grid grid-cols-2 gap-12">
                {FLOOR_HEADS.map((f, i) => (
                    <AdvancedFloorCard
                        key={f.key}
                        floor={f}
                        value={i === 0 ? floorData.GMIT : floorData.ALTF}
                        achievement={Math.round(
                            ((i === 0 ? floorData.GMIT : floorData.ALTF) / f.target) * 100,
                        )}
                        isWinner={win === (i === 0 ? "A" : "B")}
                        isLoser={win === (i === 0 ? "B" : "A")}
                        isDraw={win === "DRAW"}
                        stats={i === 0 ? floorAgentStats.GMIT : floorAgentStats.ALTF}
                    />
                ))}
            </div>
        </SlideWrapper>
    );
}

const SlideWrapper = ({ children }) => (
    <div className="min-h-screen flex items-center justify-center px-5 py-5">
        <AnimatePresence mode="wait">
            <motion.div
                key="slide"
                initial={{ opacity: 0, y: 60, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -60, scale: 0.98 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="w-full rounded-[40px] p-12 bg-white shadow-[0_30px_80px_rgba(0,0,0,0.12)] border border-gray-200 text-gray-900"
            >
                {children}
            </motion.div>
        </AnimatePresence>
    </div>
);

const ManagerCard = ({
    id,
    value,
    status,
    managerData,
    loginData,
    position,
}) => {
    console.log("loginData", loginData);
    const m = MANAGER_META[id];
    const pct = Math.min(100, Math.round((value / m.target) * 100));
    const { color, text } = getProgressMeta(pct);
    const remaining = Math.max(m.target - value, 0);

    console.log("managerData", managerData);
    const badge = getBadge(position);

    const total = loginData?.total_agents || 0;
    const loggedIn = loginData?.today_login_count || 0;

    const loginPercentage = total ? (loggedIn / total) * 100 : 0;

    const notLoginPercentage = 100 - loginPercentage;

    return (
        <motion.div
            animate={status === "WIN" ? { scale: [1, 1.03, 1] } : {}}
            transition={{ repeat: Infinity, duration: 1.4 }}
            className="relative p-8 rounded-3xl border-4 text-center overflow-hidden"
            style={{
                borderColor: color,
                boxShadow:
                    status === "WIN"
                        ? "0 0 40px rgba(34,197,94,0.6)"
                        : "0 0 20px rgba(0,0,0,0.15)",
            }}
        >
            <StatusStrip status={status} />
            <div
                className="mx-auto w-72 h-72 rounded-full p-2 mb-6"
                style={{
                    background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
                }}
            >
                <img
                    src={m.photo}
                    className="w-full h-full rounded-full border-4 mt-3"
                    style={{ borderColor: color }}
                />
            </div>
            <h2 className="text-3xl font-black">{m.name}</h2>
            <div className="text-[96px] font-extrabold leading-none">
                <span style={{ color }}>{value}</span>
                <span className="opacity-40 text-[56px]"> / {m.target}</span>
            </div>
            <p className="mt-4 text-[34px] font-semibold text-gray-700">
                {remaining > 0
                    ? `${remaining} orders to target`
                    : "Target completed 🎉"}
            </p>

            <div className="mt-4 flex justify-center gap-4">
                {loginData && (
                    <>
                        <div className="bg-gray-50 border rounded-lg p-1 text-center px-2">
                            <p className="text-xs text-gray-500 uppercase">Total Agents</p>
                            <p className="text-xl font-bold text-gray-500">
                                {loginData.total_agents || 0}
                            </p>
                        </div>
                        <div className="bg-green-50 border border-green-900 rounded-lg p-1 px-2 text-center">
                            <p className="text-xs text-green-500 uppercase">Login Agents</p>
                            <p className="text-xl font-bold text-green-500">
                                {loginData.today_login_count || 0}
                            </p>
                        </div>

                        <div className="bg-red-50 border border-red-900 rounded-lg p-1 px-2 text-center">
                            <p className="text-xs text-red-500 uppercase">Shrinkage %</p>
                            <p className="text-xl font-bold text-red-600">
                                {total ? notLoginPercentage.toFixed(1) : 0}%
                            </p>
                        </div>
                    </>
                )}
                {managerData?.agent_count_below_3 !== undefined && (
                    <div className="bg-red-50 border border-red-900 rounded-lg p-1 px-2 text-center">
                        <p className="text-xs text-red-500 uppercase">
                            Agents Below 3 Orders
                        </p>
                        <p className="text-xl font-bold text-red-900">
                            {managerData?.agent_count_below_3 || 0}
                        </p>
                    </div>
                )}
            </div>
            <div className="h-5 bg-gray-200 rounded-full overflow-hidden mt-4">
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 1 }}
                    className="h-full rounded-full"
                    style={{ backgroundColor: color }}
                />
            </div>
            <div className="mt-4 flex justify-between items-center">
                <p className="text-[30px] font-extrabold" style={{ color }}>
                    {pct}% achieved
                </p>
                <p className="text-[30px] font-black tracking-wider" style={{ color }}>
                    {text}
                </p>
            </div>
        </motion.div>
    );
};

const StatusStrip = ({ status }) => (
    <div
        className={`absolute top-0 left-0 right-0 py-2 text-white font-bold text-xl rounded-t-3xl ${status === "WIN" ? "bg-green-600" : status === "LOSE" ? "bg-red-600" : "bg-gray-600"}`}
    >
        {status === "WIN"
            ? "🏆 WINNER"
            : status === "LOSE"
                ? "😞 LOSER"
                : "🤝 DRAW"}
    </div>
);

function LiveCommentary({ headline, sub }) {
    return (
        <motion.div
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="mb-10 w-full mx-auto rounded-3xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white shadow-[0_0_60px_rgba(99,102,241,0.8)]"
        >
            <div className="px-12 py-6 text-center">
                <motion.h2
                    animate={{ scale: [1, 1.04, 1] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                    className="text-5xl font-black tracking-wide"
                >
                    {headline}
                </motion.h2>
                <p className="mt-3 text-3xl font-bold text-yellow-300">{sub}</p>
                <motion.span
                    animate={{ opacity: [1, 0.4, 1] }}
                    transition={{ repeat: Infinity, duration: 1 }}
                    className="inline-block mt-4 bg-black/40 px-6 py-2 rounded-full text-xl font-bold"
                >
                    🔴 LIVE BATTLE
                </motion.span>
            </div>
        </motion.div>
    );
}

function AdvancedFloorCard({ floor, value, isWinner, isLoser, stats }) {
    const target = floor.target;
    const remaining = Math.max(target - value, 0);
    const pct = Math.min(100, Math.round((value / target) * 100));
    const { color, text } = getProgressMeta(pct);
    const stripStyle = isWinner
        ? "bg-green-600"
        : isLoser
            ? "bg-red-600"
            : "bg-gray-700";
    const stripText = isWinner ? "🏆 WINNER" : isLoser ? "😞 LOSER" : "🤝 DRAW";

    return (
        <motion.div
            animate={{ scale: isWinner ? [1, 1.05, 1] : 1 }}
            transition={{ repeat: isWinner ? Infinity : 0, duration: 1.3 }}
            className="relative rounded-3xl p-10 border-4 bg-white shadow-2xl"
            style={{ borderColor: color }}
        >
            <div
                className={`absolute top-0 left-0 right-0 py-3 text-center text-white font-black text-[32px] rounded-t-3xl ${stripStyle}`}
            >
                {stripText}
            </div>
            <div className="flex flex-col items-center mt-10">
                <img
                    src={floor.photo}
                    className="w-72 h-72 rounded-full border-4"
                    style={{ borderColor: color }}
                />
                <p className="mt-4 text-[52px] font-black tracking-wide">
                    {floor.name}
                </p>
            </div>
            <div className="mt-4 text-center">
                <div className="text-[80px] font-extrabold leading-none">
                    <span style={{ color }}>{value}</span>
                    <span className="opacity-40 text-[56px]"> / {target}</span>
                </div>
                <p className="mt-4 text-[34px] font-semibold text-gray-700">
                    {remaining > 0
                        ? `${remaining} orders to target`
                        : "Target completed 🎉"}
                </p>
            </div>
            <div className="mt-4 flex justify-center gap-4">
                <div className="bg-gray-50 border rounded-lg p-2 text-center px-3">
                    <p className="text-xs text-gray-500 uppercase">Total Agents</p>
                    <p className="text-2xl font-bold text-gray-700">
                        {stats?.totalAgents || 0}
                    </p>
                </div>

                <div className="bg-green-50 border border-green-900 rounded-lg p-2 px-3 text-center">
                    <p className="text-xs text-green-500 uppercase">Active</p>
                    <p className="text-2xl font-bold text-green-600">
                        {stats?.loginAgents || 0}
                    </p>
                </div>

                <div className="bg-red-50 border border-red-900 rounded-lg p-2 px-3 text-center">
                    <p className="text-xs text-red-500 uppercase">Shrinkage</p>
                    <p className="text-2xl font-bold text-red-600">
                        {stats?.shrinkage?.toFixed(1) || 0}%
                    </p>
                </div>

                <div className="bg-orange-50 border border-orange-900 rounded-lg p-2 px-3 text-center">
                    <p className="text-xs text-orange-500 uppercase">Below 3 Orders</p>
                    <p className="text-2xl font-bold text-orange-600">
                        {stats?.below3Orders || 0}
                    </p>
                </div>
            </div>
            <div className="mt-6">
                <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.8 }}
                        className="h-full rounded-full"
                        style={{ backgroundColor: color }}
                    />
                </div>
                <div className="mt-4 flex justify-between items-center">
                    <p className="text-[32px] font-extrabold" style={{ color }}>
                        {pct}% achieved
                    </p>
                    <p
                        className="text-[32px] font-black tracking-wider"
                        style={{ color }}
                    >
                        {text}
                    </p>
                </div>
            </div>
        </motion.div>
    );
}

function ManagerGroupSlide({ managers, managerDetailsMap, managerLoginMap }) {
    const leader = managers.find((m) => m.status === "WIN");

    //   console.log("managersDetails 1450",managersDetails);
    //   console.log("managersDetails",managers);
    //   const mangerData = managersDetails[leader.id];
    //   console.log("mangerData",mangerData);
    //   console.log("leader",leader);
    return (
        <SlideWrapper>
            {leader && (
                <LiveCommentary
                    headline={`🔥 ${MANAGER_META[leader.id].name} is leading`}
                    sub={`Target: ${MANAGER_META[leader.id].target} orders`}
                />
            )}

            <div className="grid grid-cols-2 gap-6 mt-10">
                {/* {managers.map((m) => (
          <ManagerCard key={m.id} id={m.id} value={m.value} status={m.status} managerData={managersDetails?.[m.id]} />
        ))} */}
                {managers.map((m, index) => {
                    {
                        /* const details = managersDetails.find(
                        (x) => x.o_manager_name === m.id,
                      ); */
                    }
                    const details = managerDetailsMap[m.id];
                    const loginData = managerLoginMap[m.id];

                    return (
                        <ManagerCard
                            key={m.id}
                            id={m.id}
                            value={m.value}
                            status={m.status}
                            managerData={details}
                            loginData={loginData}
                            position={index}
                        />
                    );
                })}
            </div>
        </SlideWrapper>
    );
}

function CampaignSlide({ floorData }) {
    // ===== WINNER LOGIC =====
    const win = floorWinnerByPercentage(
        floorData.GMIT,
        CAMPAIGN_CONFIG.targets.GMIT,
        floorData.ALTF,
        CAMPAIGN_CONFIG.targets.ALTF,
    );

    const winnerName =
        win === "A"
            ? FLOOR_HEADS[0].name
            : win === "B"
                ? FLOOR_HEADS[1].name
                : "DRAW";

    return (
        <SlideWrapper>
            {/* ===== HEADER ===== */}
            <h1 className="text-6xl font-black text-center mb-6">
                🎯 Campaign Progress
            </h1>

            {/* ===== PREDICTED WINNER ===== */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8 text-center"
            >
                <p className="text-4xl font-black text-indigo-600">
                    🔮 Predicted Winner: {winnerName}
                </p>
            </motion.div>

            {/* ===== FLOOR CARDS ===== */}
            <div className="grid grid-cols-2 gap-12">
                {FLOOR_HEADS.map((f, i) => {
                    const key = i === 0 ? "GMIT" : "ALTF";
                    const target = CAMPAIGN_CONFIG.targets[key];

                    const value = i === 0 ? floorData.GMIT : floorData.ALTF;

                    const remaining = Math.max(target - value, 0);

                    const pct = Math.min(100, Math.round((value / target) * 100));

                    const isWinner = win === (i === 0 ? "A" : "B");
                    const isLoser = win === (i === 0 ? "B" : "A");

                    return (
                        <motion.div
                            key={f.key}
                            animate={{
                                scale: isWinner ? [1, 1.04, 1] : 1,
                            }}
                            transition={{
                                repeat: isWinner ? Infinity : 0,
                                duration: 1.6,
                            }}
                            className={`
                relative rounded-3xl p-10 border-4 bg-white shadow-2xl
                ${isWinner ? "border-green-500" : "border-gray-200"}
              `}
                        >
                            {/* ===== WIN / LOSE STRIP ===== */}
                            <div
                                className={`
                  absolute top-0 left-0 right-0 py-3 text-center
                  text-white font-black text-2xl rounded-t-3xl
                  ${isWinner
                                        ? "bg-green-600"
                                        : isLoser
                                            ? "bg-red-600"
                                            : "bg-gray-600"
                                    }
                `}
                            >
                                {isWinner ? "🏆 LEADING" : isLoser ? "😞 BEHIND" : "🤝 DRAW"}
                            </div>

                            {/* ===== IMAGE ===== */}
                            <div className="flex flex-col items-center mt-12">
                                <img
                                    src={f.photo}
                                    className="w-60 h-60 rounded-full border-4 border-indigo-500"
                                />

                                <p className="mt-4 text-5xl font-black">{f.name}</p>
                            </div>

                            {/* ===== VALUES ===== */}
                            <div className="mt-6 text-center">
                                <div className="text-[80px] font-black text-indigo-600">
                                    {value}
                                    <span className="text-[40px] opacity-50"> / {target}</span>
                                </div>

                                <p className="text-3xl font-bold text-red-600 mt-2">
                                    Remaining: {remaining}
                                </p>
                            </div>

                            {/* ===== PROGRESS ===== */}
                            <div className="mt-6">
                                <div className="h-6 bg-gray-200 rounded-full overflow-hidden">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${pct}%` }}
                                        transition={{ duration: 1 }}
                                        className="h-full bg-green-600"
                                    />
                                </div>

                                <p className="text-2xl font-black text-green-700 mt-3 text-center">
                                    {pct}% Campaign Complete
                                </p>
                            </div>
                        </motion.div>
                    );
                })}
            </div>

            {/* ===== FOOTER ===== */}
            <p className="text-center mt-8 text-3xl font-black text-indigo-600">
                🏆 Winner will be announced on 31 March
            </p>
        </SlideWrapper>
    );
}
function TeamWiseCard({ data }) {
    const winner =
        data?.length > 0
            ? data.reduce((prev, curr) => (prev.orders > curr.orders ? prev : curr))
            : null;

    /* 🎆 Confetti when target achieved */
    useEffect(() => {
        if (winner && winner.orders >= winner.target) {
            confetti({
                particleCount: 300,
                spread: 120,
                origin: { y: 0.6 },
            });
        }
    }, [winner]);

    return (
        <SlideWrapper>
            {/* Arena Background Glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-purple-900 to-black opacity-20 blur-3xl" />

            <h1 className="text-7xl font-black text-center mb-16 tracking-wide">
                ⚔️ Sales Battle Arena
            </h1>

            <div className="relative grid grid-cols-2 gap-16 items-center">
                {/* VS ORB */}
                {data.length === 2 && (
                    <div className="absolute left-1/2 -translate-x-1/2 z-30">
                        {/* glow */}
                        <div className="absolute inset-0 rounded-full bg-red-500 blur-3xl opacity-60 animate-pulse" />

                        {/* spinning ring */}
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ repeat: Infinity, duration: 8, ease: "linear" }}
                            className="absolute inset-0 rounded-full border-[6px] border-red-400"
                        />

                        {/* VS core */}
                        <div className="relative w-20 h-20 flex items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-yellow-400 text-white text-6xl font-black shadow-[0_0_80px_rgba(255,0,0,0.9)]">
                            {/* VS */}⚡
                        </div>
                    </div>
                )}

                {data.map((team) => {
                    const pct = team.target
                        ? Math.round((team.orders / team.target) * 100)
                        : 0;

                    const isWinner = winner?.team_name === team.team_name;

                    return (
                        <motion.div
                            key={team.team_name}
                            initial={{ opacity: 0, y: 80 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7 }}
                            whileHover={{ scale: 1.04, y: -10 }}
                            className="relative rounded-[40px] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.35)] border-4 border-gray-300 bg-white z-20"
                        >
                            {/* WINNER NEON GLOW */}
                            {isWinner && (
                                <div className="absolute -inset-10 bg-gray-400 blur-[140px] opacity-40 animate-pulse z-0" />
                            )}

                            {/* IMAGE AREA */}
                            <div className="relative h-[600px] w-full z-10">
                                <img src={team.image} className="w-full h-full object-cover" />

                                {/* DARK GRADIENT */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

                                {/* WINNER PULSE BORDER */}
                                {/* {isWinner && (
                  <motion.div
                    animate={{ opacity: [0.3, 0.8, 0.3] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                    className="absolute inset-0 border-[6px] border-green-400 rounded-[40px]"
                  />
                )} */}

                                {/* CROWN */}
                                {isWinner && (
                                    <div className="absolute top-6 right-6 text-7xl animate-bounce">
                                        👑
                                    </div>
                                )}

                                {/* TARGET HIT */}
                                {pct >= 100 && (
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        className="absolute top-6 left-6 bg-green-500 text-white px-6 py-3 rounded-full font-black text-xl shadow-lg"
                                    >
                                        🎯 TARGET HIT
                                    </motion.div>
                                )}

                                {/* TEAM NAME */}
                                <div className="absolute bottom-8 left-8">
                                    <h2 className="text-6xl font-black text-white drop-shadow-2xl">
                                        {team.team_name}
                                    </h2>
                                </div>
                            </div>

                            {/* STATS AREA */}
                            <div className="bg-white/95 backdrop-blur-xl p-8">
                                <div className="grid grid-cols-3 items-center text-center">
                                    {/* Orders */}
                                    <div>
                                        <p className="text-gray-500 text-lg">Orders</p>
                                        <p className="text-5xl font-black text-indigo-600">
                                            {team.orders}
                                        </p>
                                    </div>

                                    {/* Target */}
                                    <div>
                                        <p className="text-gray-500 text-lg">Target</p>
                                        <p className="text-5xl font-black">{team.target}</p>
                                    </div>

                                    {/* Percentage */}
                                    <div>
                                        <p className="text-gray-500 text-lg">Achieved</p>
                                        <p className="text-5xl font-black text-green-600">{pct}%</p>
                                    </div>
                                </div>

                                {/* POWER BAR */}
                                <div className="mt-6 relative h-7 bg-gray-800 rounded-full overflow-hidden">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${pct}%` }}
                                        transition={{ duration: 1.4 }}
                                        className={`h-full ${pct >= 100
                                                ? "bg-gradient-to-r from-green-400 via-green-500 to-green-700"
                                                : "bg-gradient-to-r from-indigo-400 via-purple-500 to-indigo-700"
                                            }`}
                                    />

                                    <div className="absolute inset-0 flex items-center justify-center text-xs text-white font-bold">
                                        POWER
                                    </div>
                                </div>

                                {/* HOT STREAK */}
                                {pct > 70 && pct < 100 && (
                                    <div className="mt-4 text-center text-orange-500 text-xl font-bold animate-pulse">
                                        🔥 Hot Streak
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </SlideWrapper>
    );
}



function WinnerVideo({ winner }) {
    const containerRef = useRef(null);
    const imageRef = useRef(null);
    const textRef = useRef(null);
    const borderRef = useRef(null);

    const videoMap = {
        GMIT: "/videos/RahulWon.gif",
        ALTF: "/videos/AnandWon.gif",
        DRAW: "/videos/draw.gif",
    };

    const FloorName = {
        GMIT: "RAHUL",
        ALTF: "ANAND",
        DRAW: "BOTH FLOORS",
    };

    const gifSrc = videoMap[winner];

    useEffect(() => {
        if (!winner) return;

        // 1. Initial State
        gsap.set([containerRef.current], { opacity: 0 });
        gsap.set(imageRef.current, { scale: 0.8, opacity: 0 });
        gsap.set(textRef.current, { y: 50, opacity: 0 });

        const tl = gsap.timeline();

        // 2. Entrance Animation
        tl.to(containerRef.current, { 
            opacity: 1, 
            duration: 0.5, 
            ease: "power2.out" 
        })
        .to(imageRef.current, { 
            scale: 1, 
            opacity: 1, 
            duration: 0.8, 
            ease: "back.out(1.7)" 
        }, "-=0.2")
        .to(textRef.current, { 
            y: 0, 
            opacity: 1, 
            duration: 0.6, 
            ease: "power3.out" 
        }, "-=0.4");

        // 3. Pulsing Border Animation
        gsap.to(borderRef.current, {
            boxShadow: "0 0 60px 20px rgba(234, 179, 8, 0.6)",
            repeat: -1,
            yoyo: true,
            duration: 1.5,
            ease: "sine.inOut"
        });

    }, [winner]);

    if (!winner) return null;

    return (
        <div 
            ref={containerRef}
            className="fixed inset-0 flex items-center justify-center z-[9999] bg-black/90 backdrop-blur-md"
        >
            {/* Main Content Card */}
            <div 
                ref={borderRef}
                className="relative w-[90%] h-[85%] rounded-[40px] overflow-hidden border-8 border-yellow-500 bg-slate-900 shadow-2xl"
            >
                {/* GIF Background (Using img instead of video) */}
                <img
                    ref={imageRef}
                    src={gifSrc}
                    alt="Winner Celebration"
                    className="w-full h-full object-cover"
                />

                {/* Dark Gradient Overlay for text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />

                {/* Animated Text Container */}
                <div 
                    ref={textRef}
                    className="absolute bottom-12 left-0 right-0 text-center"
                >
                    <div className="inline-block px-10 py-4 bg-yellow-500 rounded-full shadow-[0_0_30px_rgba(234,179,8,0.5)]">
                        <h2 className="text-6xl font-black text-black tracking-tighter uppercase italic">
                            🏆 {FloorName[winner]} LEADING
                        </h2>
                    </div>
                    <p className="text-white text-2xl font-bold mt-4 tracking-[10px] uppercase opacity-80">
                        Current Floor Champion
                    </p>
                </div>
            </div>
            
            {/* Animated Particles/Background Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[100vw] h-[100vh] bg-yellow-500/10 rounded-full blur-[120px] -z-10" />
        </div>
    );
}





function ManagerWinnerVideo({ data }) {
    const containerRef = useRef(null);
    const cardRef = useRef(null);
    const imageRef = useRef(null);
    const textRef = useRef(null);
    const glowRef = useRef(null);

    const { id, battle } = data; // id: "A", "B", or "DRAW"
    const gifSrc = battle.videos[id];
    const winnerName = id === "DRAW" ? "IT'S A DEAD TIE!" : `${battle.names[id].toUpperCase()} IS THE MVP`;

    useEffect(() => {
        if (!data) return;

        // 1. Initial State - Hide everything
        gsap.set(containerRef.current, { opacity: 0 });
        gsap.set(cardRef.current, { scale: 0.7, rotation: -5, opacity: 0 });
        gsap.set(textRef.current, { y: 80, opacity: 0 });
        gsap.set(glowRef.current, { scale: 0, opacity: 0 });

        const tl = gsap.timeline();

        // 2. Entrance Animation
        tl.to(containerRef.current, { 
            opacity: 1, 
            duration: 0.4 
        })
        .to(glowRef.current, {
            scale: 1,
            opacity: 1,
            duration: 1,
            ease: "expo.out"
        }, "-=0.2")
        .to(cardRef.current, { 
            scale: 1, 
            rotation: 0, 
            opacity: 1, 
            duration: 0.8, 
            ease: "back.out(1.2)" 
        }, "-=0.6")
        .to(textRef.current, { 
            y: 0, 
            opacity: 1, 
            duration: 0.6, 
            ease: "power4.out" 
        }, "-=0.4");

        // 3. Constant Neon Pulse Animation
        gsap.to(cardRef.current, {
            boxShadow: "0 0 70px 10px rgba(124, 58, 237, 0.7)", // Purple Neon Glow
            borderColor: "#8b5cf6",
            repeat: -1,
            yoyo: true,
            duration: 1.2,
            ease: "sine.inOut"
        });

        // 4. Subtle GIF Floating Effect
        gsap.to(imageRef.current, {
            scale: 1.05,
            duration: 3,
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut"
        });

    }, [id]);

    if (!data) return null;

    return (
        <div 
            ref={containerRef}
            className="fixed inset-0 flex items-center justify-center z-[9999] bg-[#020617]/95 backdrop-blur-xl"
        >
            {/* Massive Background Neon Glow */}
            <div 
                ref={glowRef}
                className="absolute w-[800px] h-[1200px] bg-indigo-600/20 rounded-full blur-[160px] -z-10" 
            />

            {/* Main Content Card */}
            <div 
                ref={cardRef}
                className="relative w-[90%] h-[85%] rounded-[50px] overflow-hidden border-8 border-indigo-500 bg-slate-950 shadow-[0_0_50px_rgba(0,0,0,0.5)]"
            >
                {/* GIF Background */}
                <img
                    ref={imageRef}
                    src={gifSrc}
                    alt="Manager Celebration"
                    className="w-full h-full object-cover"
                />

                {/* Cyberpunk Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-indigo-900/20" />

                {/* Animated Text Container */}
                <div 
                    ref={textRef}
                    className="absolute bottom-12 left-0 right-0 text-center"
                >
                    <div className="inline-block px-12 py-5 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl shadow-[0_0_40px_rgba(99,102,241,0.6)] border border-white/20">
                        <h2 className="text-7xl font-black text-white tracking-tight uppercase italic drop-shadow-lg">
                            {winnerName}
                        </h2>
                    </div>
                    <p className="text-indigo-300 text-2xl font-black mt-6 tracking-[12px] uppercase opacity-90 animate-pulse">
                        🔥 MANAGER BATTLE VICTOR 🔥
                    </p>
                </div>
            </div>
            
            {/* Top Corner Badge */}
            <div className="absolute top-10 left-10 bg-white/10 backdrop-blur-md px-6 py-2 rounded-full border border-white/20">
                <span className="text-white font-bold text-xl tracking-widest">LIVE RESULTS</span>
            </div>
        </div>
    );
}